import React, { createContext, useContext, useState, useEffect } from 'react';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only setup the native event listener, but don't request browser location automatically
    if (window.isUmmahtiNative) {
      const handleLocationUpdate = (event) => {
        if (!event?.detail?.coords) return;
        const { coords } = event.detail;
        if (typeof coords.latitude !== 'number' || typeof coords.longitude !== 'number') return;
        setLocation(coords);
      };

      window.addEventListener('native-location-update', handleLocationUpdate);
      return () => {
        window.removeEventListener('native-location-update', handleLocationUpdate);
      };
    }
  }, []);

  // New function to get location only when explicitly requested
  const getLocation = () => {
    if (location) return Promise.resolve(location);
    
    setLoading(true);
    return new Promise((resolve, reject) => {
      if (window.isUmmahtiNative) {
        // Actively request location from the native app
        if (window.ummahti?.requestLocation) {
          window.ummahti.requestLocation();
        } else if (window.webkit?.messageHandlers?.locationHandler?.postMessage) {
          window.webkit.messageHandlers.locationHandler.postMessage({action: 'requestLocation'});
        }
        
        // Set up a listener for the one-time response
        const locationListener = (event) => {
          if (!event?.detail?.coords) return;
          const { coords } = event.detail;
          if (typeof coords.latitude !== 'number' || typeof coords.longitude !== 'number') return;
          
          // Remove the one-time listener
          window.removeEventListener('native-location-update', locationListener);
          
          // Update state and resolve promise
          setLocation(coords);
          setLoading(false);
          resolve(coords);
        };
        
        // Add the one-time listener
        window.addEventListener('native-location-update', locationListener);
        
        // Set a timeout in case the native app doesn't respond
        setTimeout(() => {
          // Remove the listener to avoid memory leaks
          window.removeEventListener('native-location-update', locationListener);
          
          if (!location) {
            setError('Unable to get location from native app');
            setLoading(false);
            reject(new Error('Unable to get location from native app'));
          }
        }, 5000); // Increased timeout to 5 seconds
      } else if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation(position.coords);
            setLoading(false);
            resolve(position.coords);
          },
          (err) => {
            setError(err.message);
            setLoading(false);
            reject(err);
          }
        );
      } else {
        const err = new Error('Geolocation is not supported by your browser');
        setError(err.message);
        setLoading(false);
        reject(err);
      }
    });
  };

  return (
    <LocationContext.Provider value={{ location, error, loading, getLocation }}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext; 